import {
  Checkbox,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  Input,
  Select,
  type SelectOptionType,
} from '@bp/ui-components';
import { graphql } from 'client/gql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { Form, Formik } from 'formik';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';
import { useMutation, useQuery } from 'urql';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { bookedBBBSlots } from '../../../utils/bookedBBBSlots';
import { ensureDate } from '../../../utils/dateCalculations';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { validationSchema } from './bigBlueButtonForm.schema';

export const CreateBigBlueButtonsMutationDocument = graphql(`
  mutation CreateBigBlueButtons($input: [BigBlueButtonCreateInput!]!) {
    createBigBlueButtons(input: $input) {
      bigBlueButtons {
        uuid
        __typename
      }
    }
  }
`);

const UpdateBigBlueButtonsMutationDocument = graphql(`
  mutation UpdateBigBlueButtons($update: BigBlueButtonUpdateInput!, $where: BigBlueButtonWhere!) {
    updateBigBlueButtons(where: $where, update: $update) {
      bigBlueButtons {
        uuid
        __typename
        apiUrl
        allowRecording
        maxParticipantsPerMeeting
        organizationConnection {
          __typename
          edges {
            __typename
            properties {
              __typename
              notAfter
              notBefore
              version
            }
          }
        }
      }
    }
  }
`);

const SettingsBigBlueButtonFormQueryDocument = graphql(`
  query SettingsBigBlueButtonFormData($uuid: ID!) {
    bigBlueButtons(where: { uuid: $uuid }) {
      uuid
      __typename
      apiUrl
      maxParticipantsPerMeeting
      allowRecording
      organizationConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            notAfter
            notBefore
            version
            contractRef
          }
        }
      }
    }
  }
`);

type BBBValuesType = {
  apiUrl?: string | null;
  apiSecret?: string | null;
  allowRecording?: boolean | null;
  version?: number | null;
  notBefore: string;
  notAfter?: string;
  maxParticipantsPerMeeting: number | null;
};

type BigBlueButtonFormProps = {
  onClose: () => void;
};

export const BigBlueButtonForm = ({ onClose }: BigBlueButtonFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { organizationProducts, bildungsplattformVersion } = useContext(OrganizationConfigContext);

  const bbbUuid = organizationProducts?.find((d) => d.__typename === 'BigBlueButton')?.uuid;

  const [{ data: queryData }] = useQuery({
    query: SettingsBigBlueButtonFormQueryDocument,
    variables: {
      uuid: bbbUuid ?? '',
    },
  });

  const permissions = usePermissionChecker();

  const [, updateBBBs] = useMutation(UpdateBigBlueButtonsMutationDocument);
  const [, createBBBs] = useMutation(CreateBigBlueButtonsMutationDocument);

  const proOpts = [
    {
      label: t('bigBlueButton.pro.option_1'),
      value: 50,
    },
    {
      label: t('bigBlueButton.pro.option_2'),
      value: 150,
    },
    {
      label: t('bigBlueButton.pro.option_3'),
      value: 300,
    },

    {
      label: t('bigBlueButton.pro.option_4'),
      value: 550,
    },
    {
      label: t('bigBlueButton.pro.option_5'),
      value: 800,
    },
    {
      label: t('bigBlueButton.pro.option_6'),
      value: 1050,
    },
    {
      label: t('bigBlueButton.pro.option_7'),
      value: 0,
    },
  ];

  const notProOpts = [
    {
      label: t('bigBlueButton.notPro.option_1'),
      value: 100,
    },
    {
      label: t('bigBlueButton.notPro.option_2'),
      value: 250,
    },
    {
      label: t('bigBlueButton.notPro.option_3'),
      value: 500,
    },

    {
      label: t('bigBlueButton.notPro.option_4'),
      value: 750,
    },
    {
      label: t('bigBlueButton.notPro.option_5'),
      value: 1000,
    },
    {
      label: t('bigBlueButton.notPro.option_6'),
      value: 0,
    },
  ];

  const currentBooking = queryData?.bigBlueButtons[0];
  const currentBookingProps = currentBooking?.organizationConnection.edges[0]?.properties;

  const selectOpts: SelectOptionType[] = bildungsplattformVersion === 'pro' ? proOpts : notProOpts;

  const initialValues: BBBValuesType = {
    apiUrl: currentBooking?.apiUrl ?? '',
    apiSecret: '',
    allowRecording: currentBooking?.allowRecording ?? false,
    version: bookedBBBSlots(currentBookingProps?.version),
    notBefore: ensureDate(currentBookingProps?.notBefore).toUTCString(),
    notAfter: currentBookingProps?.notAfter ? ensureDate(currentBookingProps?.notAfter).toUTCString() : undefined,
    maxParticipantsPerMeeting: currentBooking?.maxParticipantsPerMeeting ?? null,
  };

  const onSubmit = async (values: BBBValuesType) => {
    if (permissions?.canUpdateBBB({ uuid: organizationUuid })) {
      if (bbbUuid === undefined) {
        const resp = await createBBBs({
          input: [
            {
              apiUrl: values.apiUrl,
              // apiSecret: values.apiSecret,
              allowRecording: values.allowRecording,
              maxParticipantsPerMeeting: values.maxParticipantsPerMeeting,
              organization: {
                connect: {
                  overwrite: true,
                  edge: {
                    notBefore: values.notBefore,
                    notAfter: values.notAfter ?? null,
                    version: values.version?.toString(),
                  },
                  where: { node: { uuid: organizationUuid } },
                },
              },
            },
          ],
        });
        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      } else {
        const resp = await updateBBBs({
          update: {
            apiUrl: values.apiUrl,
            // apiSecret: values.apiSecret,
            allowRecording: values.allowRecording,
            maxParticipantsPerMeeting: values.maxParticipantsPerMeeting,
            organization: {
              update: {
                edge: {
                  notBefore: values.notBefore,
                  notAfter: values.notAfter ?? null,
                  version: values.version?.toString(),
                },
              },
            },
          },
          where: { uuid: bbbUuid },
        });
        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      }
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ resetForm, setFieldValue, handleChange, setFieldTouched, handleBlur, values, isSubmitting, errors }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow>
                <GridColumn width={9}>
                  <GridRow>
                    <GridColumn width={4}>
                      <DatePicker
                        label={t('institution.bookedFromDate')}
                        onChange={(e) => setFieldValue('notBefore', e?.toUTCString())}
                        value={ensureDate(values.notBefore)}
                        name={'notBefore'}
                        showMonthYearDropdown
                      />
                    </GridColumn>
                    <GridColumn width={4}>
                      <DatePicker
                        label={t('institution.bookedUntilDate')}
                        onChange={async (e) => {
                          await setFieldValue('notAfter', e?.toUTCString());
                        }}
                        value={values.notAfter ? ensureDate(values.notAfter) : undefined}
                        name={'notAfter'}
                        showMonthYearDropdown
                      />
                    </GridColumn>
                    <GridColumn width={4}>
                      <Select
                        options={selectOpts}
                        onChange={async (event) => {
                          const a: SelectOptionType | null = event as SingleValue<SelectOptionType>;
                          await setFieldTouched('version', true);
                          await setFieldValue('version', a?.value);
                        }}
                        value={selectOpts.find((s) => s.value === values.version)}
                        name={'version'}
                        label={t('settings.bbbNumberConnections')}
                      />
                    </GridColumn>
                  </GridRow>
                </GridColumn>
                <GridColumn width={3} align='end'>
                  <Checkbox
                    className='mt-3'
                    label={t('settings.bbbAllowRecordings')}
                    name={'allowRecording'}
                    onChange={handleChange}
                    checked={values.allowRecording ?? false}
                    error={errors.allowRecording}
                  />
                </GridColumn>
              </GridRow>

              <GridRow headline={t('settings.bbbCustomInfo')} direction='column'>
                <GridRow spacingBottom='s'>
                  <Input
                    label={t('settings.maxParticipantsPerMeeting')}
                    name={'maxParticipantsPerMeeting'}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.maxParticipantsPerMeeting}
                    error={errors.maxParticipantsPerMeeting}
                  />
                </GridRow>
                <GridRow spacingBottom='s' spacingTop='s'>
                  <Input
                    label={t('settings.bbbApiUrl')}
                    name={'apiUrl'}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.apiUrl}
                    error={errors.apiUrl}
                  />
                </GridRow>
                <GridRow spacingTop='s'>
                  <Input
                    label={t('settings.bbbApiSecret')}
                    name={'apiSecret'}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.apiSecret}
                    error={errors.apiSecret}
                  />
                </GridRow>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              errors={errors}
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              isLoading={isSubmitting}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
